import {SessionMode} from "play/play-model"

export enum UserKind {
  Anonymous = "A", 
  Student = "S", 
  Teacher = "T"
}

export interface GameCode {
  code: number
  mode: SessionMode
  isNvo: boolean
  lessonId?: string|null
  isDemo: boolean
}

export interface ConnectionId {
  gameCode: GameCode
  userKind: UserKind
  userId: string|null
  fullName: string|null
  playthroughId: string|null
}

// 5654baa1-b9e0-4ae0-af1e-e8e6e89e61a9
const isGuid1Regex = /([a-fA-F0-9]){8}-([a-fA-F0-9]){4}-([a-fA-F0-9]){4}-([a-fA-F0-9]){4}-([a-fA-F0-9]){12}/
// 5654baa1b9e04ae0af1ee8e6e89e61a9
const isGuid2Regex = /([a-fA-F0-9]){8}([a-fA-F0-9]){4}([a-fA-F0-9]){4}([a-fA-F0-9]){4}([a-fA-F0-9]){12}/
// {5654baa1-b9e0-4ae0-af1e-e8e6e89e61a9}
const isGuid3Regex = /\{([a-fA-F0-9]){8}-([a-fA-F0-9]){4}-([a-fA-F0-9]){4}-([a-fA-F0-9]){4}-([a-fA-F0-9]){12}\}/

export function isGuid(input: string|null|undefined) {
  if (!input) return false

  return isGuid1Regex.test(input) || isGuid2Regex.test(input) || isGuid3Regex.test(input)
}

export function tryParseGameCode(input: string|null|undefined): GameCode|null {
  if (!input) return null

  if (isGuid(input)) {
    return {
      code: 0,
      mode: SessionMode.Homework,
      isNvo: false,
      lessonId: input,
      isDemo: true
    } 
  }

  const code = Number(input);
  if (isNaN(code) || code === 0) return null

  const isNvo = code === 1111 || code === 3333 || code === 5555 || code === 7777
  let mode: SessionMode
  if (isNvo) {
    mode = SessionMode.Homework
  } else {
    mode = code % 2 === 0 ? SessionMode.Classroom : SessionMode.Homework
  }

  return {
    code,
    mode,
    isNvo,
    lessonId: null,
    isDemo: false
  }
}

function partsParser(input: string) {
  const parts = input.split('_')
  let idx = 0;

  return {
    next: function() {
      if (parts.length < idx) { return null }
      return parts[idx++]
    }
  }  
}

export function tryParseConnectionId(input: string|null|undefined): ConnectionId|null {
  if (!input) return null

  const parts = partsParser(input)

  const magick = parts.next()
  if (magick !== "CID") return null

  const gameCode = tryParseGameCode(parts.next())
  if (!gameCode) return null

  const maybeUserKind = parts.next()
  let userKind: UserKind
  if (maybeUserKind === "S")
    userKind = UserKind.Student
  else if (maybeUserKind === "T") 
    userKind = UserKind.Teacher
  else if (maybeUserKind === "A")
    userKind = UserKind.Anonymous
  else
    return null

  if (gameCode.isNvo) {
    if (userKind === UserKind.Student || userKind === UserKind.Teacher) {
      const userId = parts.next()
      if (!isGuid(userId)) return null

      const playthroughId = parts.next()
      if (!isGuid(playthroughId)) return null

      return {
        gameCode,
        userKind,
        userId,
        fullName: null,
        playthroughId
      }
    } else {
      const playthroughId = parts.next()
      if (!isGuid(playthroughId)) return null

      const fullName = parts.next()
      if (!fullName || fullName.length < 3) return null

      return {
        gameCode,
        userKind,
        userId: null,
        fullName,
        playthroughId
      }
    }
  } else if (gameCode.isDemo) {
    if (userKind === UserKind.Student || userKind === UserKind.Teacher) {
      const userId = parts.next()
      if (!isGuid(userId)) return null

      return {
        gameCode,
        userKind,
        userId,
        fullName: null,
        playthroughId: null
      }
    } else {
      const playthroughId = parts.next()
      if (!isGuid(playthroughId)) return null

      const fullName = parts.next()
      if (!fullName || fullName.length < 3) return null

      return {
        gameCode,
        userKind,
        userId: null,
        fullName,
        playthroughId
      }
    }
  } else {
    if (userKind === UserKind.Student || userKind === UserKind.Teacher) {
      const userId = parts.next()
      if (!isGuid(userId)) return null

      return {
        gameCode,
        userKind,
        userId,
        fullName: null,
        playthroughId: null
      }
    } else {
      const fullName = parts.next()
      if (!fullName || fullName.length < 3) return null

      return {
        gameCode,
        userKind,
        userId: null,
        fullName,
        playthroughId: null
      }
    }
  }
}